import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PageHeader } from "@/components/admin/PageHeader";
import { ResourceState } from "@/components/admin/ResourceState";
import { useDashboardAnalytics } from "@/application/hooks/use-dashboard-analytics";
import { useI18n } from "@/i18n";

const COLORS = ["#4EA8FF", "#7EC7FF", "#2C7DE0", "#A9D5FF", "#5C6B85", "#8CA6C7"];

export function AnalyticsPage() {
  const { t } = useI18n();
  const { status, data, error, reload } = useDashboardAnalytics();

  return (
    <div>
      <PageHeader title={t.admin.analytics.title} description={t.admin.analytics.description} />
      <ResourceState status={status} error={error} onRetry={() => void reload()}>
        {data ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title={t.admin.analytics.daysOnMarket}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.revenueByMonth}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border) / 0.3)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="units"
                    stroke="#4EA8FF"
                    strokeWidth={2.5}
                    dot={{ fill: "#4EA8FF", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card title={t.admin.analytics.bodyStyle}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={data.bodyDistribution}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {data.bodyDistribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card title={t.admin.analytics.monthlyAdditions} className="lg:col-span-2">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={data.brandDistribution.map((b) => ({
                    ...b,
                    adds: Math.round(b.value * 2.3),
                  }))}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border) / 0.3)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="adds" radius={[6, 6, 0, 0]} fill="#4EA8FF" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        ) : null}
      </ResourceState>
    </div>
  );
}

function Card({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-border/60 bg-surface-elevated/60 p-5 backdrop-blur ${className}`}
    >
      <h3 className="mb-4 font-display text-base font-semibold">{title}</h3>
      {children}
    </div>
  );
}
